<template>
  <div>
    <div v-if="place && !loading">
      <page-title-area>
        <h1>{{ place.name }}</h1>
      </page-title-area>

      <floor-plan-toolbar
        v-if="currentFloorPlan"
        class="floor-plan-toolbar"
        :current-floor-plan="currentFloorPlan"
        :all-floor-plans="allFloorPlans"
        :selected-month="selectedMonth"
        :insect-filter="selectedInsectFilter"
        :display-mode="displayMode"
        :loading-events="loadingEvents"
        :has-unsaved-changes="hasUnsavedChanges"
        :deleting="deleting"
        :saving="saving"
        :exporting="exporting"
        @change="handleFloorPlanSwitch"
        @save-name="handleSaveName"
        @save="saveChanges"
        @cancel="cancelChanges"
        @add="handleAdd"
        @delete="handleDelete"
        @download="handleDownload"
        @month-change="handleMonthChange"
        @insect-filter-change="handleInsectFilterChange"
        @display-mode-change="displayMode = $event"
      />

      <input
        ref="fileInput"
        type="file"
        accept=".jpg,.jpeg,.png"
        style="display: none"
        :aria-label="$t('aria.floor_plan_file_input')"
        @change="handleFileSelected"
      />

      <!-- アップロード中の表示 -->
      <v-alert v-if="isUploadingFloorPlan" type="info" dense class="mb-4">
        {{ $t('msg.upload_processing') }}
      </v-alert>

      <v-row v-if="currentFloorPlan">
        <v-col cols="12" md="8">
          <floor-plan-canvas
            ref="floorPlanCanvas"
            :floor-plan="currentFloorPlan"
            :traps="trapList"
            :display-mode="displayMode"
            :place-name="place ? place.name : ''"
            :selected-month="selectedMonth"
            :insect-filter-label="getInsectFilterLabel()"
            @change="handleFloorPlanChange"
          />
        </v-col>
        <v-col cols="12" md="4">
          <trap-panel
            v-if="currentFloorPlan"
            :key="currentFloorPlan.id"
            :traps="trapList"
            :events="events"
            :place-name="place ? place.name : ''"
            :floor-plan-name="currentFloorPlan ? currentFloorPlan.name : ''"
            :selected-month="selectedMonth"
            :insect-filter-label="getInsectFilterLabel()"
            @remove="handleRemoveTrap"
          />
        </v-col>
      </v-row>

      <v-row v-else-if="!isUploadingFloorPlan">
        <v-col cols="12">
          <v-card class="text-center pa-8">
            <v-icon size="80" color="grey lighten-1">
              mdi-floor-plan
            </v-icon>
            <h2 class="mt-4 mb-2">
              {{ $t('empty.title') }}
            </h2>
            <p class="text--secondary mb-4">
              {{ $t('empty.description') }}
            </p>
            <v-btn color="primary" large @click="handleAdd">
              <v-icon left>
                mdi-upload
              </v-icon>
              {{ $t('empty.upload_button') }}
            </v-btn>
          </v-card>
        </v-col>
      </v-row>
    </div>
    <page-load-spinner v-else />

    <!-- ポーリングチャンネル -->
    <polling-channel
      v-if="pollingForNewFloorPlan"
      :input="pollingState"
      :active="pollingForNewFloorPlan"
      :updateFunction="checkForNewFloorPlan"
      :interval="POLLING_INTERVAL_MS"
      @valueUpdated="onFloorPlanPollingUpdate"
    />
  </div>
</template>

<i18n lang="yaml">
ja:
  msg.load.failed: |
    データの取得に失敗しました。
    再度お試しください。
  msg.save.success: 図面を保存しました
  msg.save.failed: 図面の保存に失敗しました
  msg.save.duplicate_trap: 'トラップ {trapNos} は他の図面に既に配置されています'
  msg.delete.success: 図面を削除しました
  msg.delete.failed: 図面の削除に失敗しました
  msg.upload.success: 図面のアップロードが完了しました
  msg.upload.failed: アップロードに失敗しました。しばらくしてから再度お試しください。
  msg.upload_processing: 図面をアップロード中...
  msg.upload_timeout: 図面の追加がタイムアウトしました
  msg.upload.duplicate_name: 同じ名前の図面が既に存在します
  msg.save.duplicate_name: この図面名は既に使用されています
  confirm.leave_page: 未保存の変更があります。このページを離れますか？
  confirm.delete_floor_plan: この図面を削除しますか？
  confirm.discard_changes: 未保存の変更があります。破棄しますか？
  validation.invalid_file_type: JPEG/PNG形式のファイルのみアップロードできます
  validation.image_size_too_large: 画像サイズが大きすぎます
  aria.floor_plan_file_input: 図面ファイル選択
  empty.title: 図面がまだ登録されていません
  empty.description: 現場の図面をアップロードして、トラップ配置を管理しましょう
  empty.upload_button: 図面をアップロード
  msg.download.failed: 画像のダウンロードに失敗しました
  insect_filter:
    all: 全て
    indoor: 内部発生
    outdoor: 外部侵入
    other: その他

en:
  msg.load.failed: |
    Failed to load data.
    Please try again.
  msg.save.success: Floor plan saved successfully
  msg.save.failed: Failed to save floor plan
  msg.save.duplicate_trap: 'Trap {trapNos} is already placed on another floor plan'
  msg.delete.success: Floor plan deleted successfully
  msg.delete.failed: Failed to delete floor plan
  msg.upload.success: Floor plan uploaded successfully
  msg.upload.failed: Upload failed. Please try again later.
  msg.upload_processing: Uploading floor plan...
  msg.upload_timeout: Floor plan upload timed out
  msg.upload.duplicate_name: A floor plan with this name already exists
  msg.save.duplicate_name: This floor plan name is already in use
  confirm.leave_page: You have unsaved changes. Do you want to leave this page?
  confirm.delete_floor_plan: Are you sure you want to delete this floor plan?
  confirm.discard_changes: You have unsaved changes. Discard them?
  validation.invalid_file_type: Only JPEG/PNG format files can be uploaded
  validation.image_size_too_large: Image size is too large
  aria.floor_plan_file_input: Floor plan file input
  empty.title: No floor plans yet
  empty.description: Upload a floor plan to manage trap placements
  empty.upload_button: Upload Floor Plan
  msg.download.failed: Failed to download image
  insect_filter:
    all: All
    indoor: Indoor
    outdoor: Outdoor
    other: Other
</i18n>

<script>
import { parse, subMonths, subYears } from 'date-fns';
import Notifications from '@/mixins/notifications';
import { flyingInsectTypeMap } from '@/libs/insectTypes';
import dateTimeFormatting from '@/mixins/dateTimeFormatting';
import { validateImageFile } from '@/libs/imageValidator';
import PageTitleArea from '@/components/atoms/PageTitleArea';
import PageLoadSpinner from '@/components/atoms/PageLoadSpinner';
import PollingChannel from '@/components/atoms/PollingChannel';
import { DISPLAY_MODES } from './displayModes';
import FloorPlanCanvas from './FloorPlanCanvas';
import FloorPlanToolbar from './FloorPlanToolbar';
import TrapPanel from './TrapPanel';

const MAX_FLOOR_PLAN_IMAGE_SIZE = 10000; // 図面画像の最大長辺ピクセル数
const POLLING_INTERVAL_MS = 2000; // ポーリング間隔（ミリ秒）
const POLLING_TIMEOUT_MS = 60000; // ポーリングタイムアウト（60秒）

export default {
  name: 'StationPlaceFloorPlans',
  inject: ['api'],
  mixins: [
    Notifications,
    dateTimeFormatting,
  ],
  components: {
    PageTitleArea,
    PageLoadSpinner,
    PollingChannel,
    FloorPlanCanvas,
    FloorPlanToolbar,
    TrapPanel,
  },
  props: {
    placeId: {
      type: String,
    },
  },
  data() {
    return {
      place: null,
      currentFloorPlan: null,
      allFloorPlans: [],
      originalFloorPlan: null,
      events: [],
      previousMonthEvents: [],
      lastYearMonthEvents: [],
      lastYearPreviousMonthEvents: [],
      selectedMonth: this.formatMonthForApi(new Date()),
      selectedInsectFilter: 'all',
      displayMode: DISPLAY_MODES.NONE,
      uploading: false,
      deleting: false,
      saving: false,
      loadingEvents: false,
      pollingForNewFloorPlan: false,
      pollingState: null,
      pollingStartTime: null,
      loading: true,
      exporting: false,
      POLLING_INTERVAL_MS,
    };
  },
  computed: {
    previousMonth() {
      const monthDate = parse(this.selectedMonth, 'yyyy-MM', new Date());
      return this.formatMonthForApi(subMonths(monthDate, 1));
    },
    lastYearMonth() {
      const monthDate = parse(this.selectedMonth, 'yyyy-MM', new Date());
      return this.formatMonthForApi(subYears(monthDate, 1));
    },
    lastYearPreviousMonth() {
      const monthDate = parse(this.selectedMonth, 'yyyy-MM', new Date());
      return this.formatMonthForApi(subMonths(subYears(monthDate, 1), 1));
    },
    trapList() {
      if (!this.place || !this.place.trap_nos) {
        return [];
      }

      return this.place.trap_nos.map((trapNo) => ({
        trapNo,
        coordinates: this.currentFloorPlan?.objects?.[trapNo] || null,
        isPlaced: !!this.currentFloorPlan?.objects?.[trapNo],
        isPlacedOnOtherFloorPlan: this.isPlacedOnOtherFloorPlan(trapNo),
        placedFloorPlanName: this.getPlacedFloorPlanName(trapNo),
        count: this.getCountForTrap(trapNo),
        previousMonthCount: this.getCountForTrap(trapNo, this.previousMonthEvents),
        lastYearMonthCount: this.getCountForTrap(trapNo, this.lastYearMonthEvents),
        lastYearPreviousMonthCount: this.getCountForTrap(trapNo, this.lastYearPreviousMonthEvents),
      }));
    },
    hasNameChanged() {
      if (!this.currentFloorPlan || !this.originalFloorPlan) {
        return false;
      }
      return this.currentFloorPlan.name !== this.originalFloorPlan.name;
    },
    hasObjectsChanged() {
      if (!this.currentFloorPlan || !this.originalFloorPlan) {
        return false;
      }
      return JSON.stringify(this.currentFloorPlan.objects)
        !== JSON.stringify(this.originalFloorPlan.objects);
    },
    hasUnsavedChanges() {
      return this.hasNameChanged || this.hasObjectsChanged;
    },
    isUploadingFloorPlan() {
      return this.uploading || this.pollingForNewFloorPlan;
    },
  },
  methods: {
    confirmDiscardChanges() {
      if (!this.hasUnsavedChanges) return true;

      // eslint-disable-next-line no-alert
      return window.confirm(this.$t('confirm.discard_changes'));
    },
    isPlacedOnOtherFloorPlan(trapNo) {
      const currentFloorPlanId = this.currentFloorPlan?.id;
      if (!currentFloorPlanId) return false;
      return this.allFloorPlans.some(fp => fp.id !== currentFloorPlanId && fp.objects?.[trapNo]);
    },
    getPlacedFloorPlanName(trapNo) {
      const currentFloorPlanId = this.currentFloorPlan?.id;
      if (!currentFloorPlanId) return null;
      const floorPlan = this.allFloorPlans.find(
        fp => fp.id !== currentFloorPlanId && fp.objects?.[trapNo],
      );
      return floorPlan?.name || null;
    },
    getInsectCountByRoute(insectCounts, route) {
      const insectTypes = flyingInsectTypeMap(this.$i18n.locale);
      return Object.entries(insectCounts).reduce(
        (acc, [insectType, count]) => (insectTypes[insectType]?.route === route ? acc + count : acc),
        0,
      );
    },
    getCountForTrap(trapNo, events = this.events) {
      const trapEvents = events.filter(event => event.trap_no === trapNo);
      return trapEvents.reduce((sum, event) => {
        const counts = event.insect_counts || {};
        const filter = this.selectedInsectFilter;

        if (filter === 'all') {
          return sum + this._.sum(Object.values(counts));
        }
        if (filter === 'indoor' || filter === 'outdoor') {
          return sum + this.getInsectCountByRoute(counts, filter);
        }
        if (filter === 'other') {
          return sum + (counts.others || 0);
        }
        return sum + (counts[filter] || 0);
      }, 0);
    },
    handleInsectFilterChange(newFilter) {
      this.selectedInsectFilter = newFilter;
    },
    async handleDownload() {
      this.exporting = true;
      try {
        await this.$refs.floorPlanCanvas.exportImage();
      } catch (error) {
        this.notifyError('msg.download.failed');
      } finally {
        this.exporting = false;
      }
    },
    getInsectFilterLabel() {
      const filter = this.selectedInsectFilter;
      // 特殊フィルタはi18nから取得
      if (['all', 'indoor', 'outdoor', 'other'].includes(filter)) {
        return this.$t(`insect_filter.${filter}`);
      }
      // 個別虫種の場合は飛翔種マップ（flyingInsectTypeMap）の label を返す
      const insectType = flyingInsectTypeMap(this.$i18n.locale)[filter];
      if (insectType && insectType.label) {
        return insectType.label;
      }
      // 飛翔種マップに存在しないフィルタは「その他」にフォールバック
      return this.$t('insect_filter.other');
    },
    async handleMonthChange(newMonth) {
      const savedMonth = this.selectedMonth;
      this.selectedMonth = newMonth;
      this.loadingEvents = true;
      try {
        this.events = await this.api.fetchStationPlaceEvents(this.placeId, {
          from: newMonth,
          to: newMonth,
        });
        // ホバーポップアップ用に常に比較データを取得
        await this.fetchComparisonData();
      } catch (error) {
        this.selectedMonth = savedMonth; // 元の値に戻す
        this.notifyError('msg.load.failed');
      } finally {
        this.loadingEvents = false;
      }
    },
    async fetchComparisonData() {
      this.loadingEvents = true;
      try {
        const [prevEvents, lastYearEvents, lastYearPrevEvents] = await Promise.all([
          this.api.fetchStationPlaceEvents(this.placeId, {
            from: this.previousMonth,
            to: this.previousMonth,
          }),
          this.api.fetchStationPlaceEvents(this.placeId, {
            from: this.lastYearMonth,
            to: this.lastYearMonth,
          }),
          this.api.fetchStationPlaceEvents(this.placeId, {
            from: this.lastYearPreviousMonth,
            to: this.lastYearPreviousMonth,
          }),
        ]);
        this.previousMonthEvents = prevEvents;
        this.lastYearMonthEvents = lastYearEvents;
        this.lastYearPreviousMonthEvents = lastYearPrevEvents;
      } catch (error) {
        this.previousMonthEvents = [];
        this.lastYearMonthEvents = [];
        this.lastYearPreviousMonthEvents = [];
        this.notifyError('msg.load.failed');
      } finally {
        this.loadingEvents = false;
      }
    },
    handleFloorPlanChange({ trapNo, coordinates }) {
      if (!this.currentFloorPlan.objects) {
        this.currentFloorPlan.objects = {};
      }

      this.$set(this.currentFloorPlan.objects, trapNo, coordinates);
    },
    handleRemoveTrap(trapNo) {
      this.$delete(this.currentFloorPlan.objects, trapNo);
    },
    async saveChanges() {
      this.saving = true;
      try {
        const objects = this.buildObjectsFromTraps();
        const updated = await this.api.updateFloorPlan(this.currentFloorPlan.id, { objects });

        this.updateFloorPlanState(updated);
        this.notifySuccess('msg.save.success');
      } catch (error) {
        if (error.response?.data?.error === 'duplicate_trap_no') {
          const trapNos = error.response.data.trap_nos.join(', ');
          this.notifyError('msg.save.duplicate_trap', { trapNos });
        } else {
          this.notifyError('msg.save.failed');
        }
      } finally {
        this.saving = false;
      }
    },
    cancelChanges() {
      // 元のデータに復元
      this.currentFloorPlan = JSON.parse(JSON.stringify(this.originalFloorPlan));

      // allFloorPlans配列内の対応する図面も元に戻す
      const index = this.allFloorPlans.findIndex(fp => fp.id === this.originalFloorPlan.id);
      if (index !== -1) {
        this.$set(this.allFloorPlans, index, JSON.parse(JSON.stringify(this.originalFloorPlan)));
      }
    },
    handleFloorPlanSwitch(floorPlanId) {
      // 同じ図面を選択した場合は何もしない
      if (this.currentFloorPlan && this.currentFloorPlan.id === floorPlanId) {
        return;
      }

      if (!this.confirmDiscardChanges()) return;

      const selectedFloorPlan = this.allFloorPlans.find(fp => fp.id === floorPlanId);
      if (selectedFloorPlan) {
        this.currentFloorPlan = JSON.parse(JSON.stringify(selectedFloorPlan));
        this.originalFloorPlan = JSON.parse(JSON.stringify(selectedFloorPlan));
      }
    },
    async handleDelete() {
      if (!this.currentFloorPlan) return;

      // eslint-disable-next-line no-alert
      const confirmed = window.confirm(this.$t('confirm.delete_floor_plan'));
      if (!confirmed) return;

      this.deleting = true;
      try {
        const deletedFloorPlanId = this.currentFloorPlan.id;
        await this.api.deleteFloorPlan(deletedFloorPlanId);

        this.switchToNextFloorPlan(deletedFloorPlanId);
        this.notifySuccess('msg.delete.success');
      } catch (error) {
        console.error('Failed to delete floor plan:', error); // eslint-disable-line no-console
        this.notifyError('msg.delete.failed');
      } finally {
        this.deleting = false;
      }
    },
    switchToNextFloorPlan(deletedFloorPlanId) {
      this.allFloorPlans = this.allFloorPlans.filter(fp => fp.id !== deletedFloorPlanId);

      if (this.allFloorPlans.length > 0) {
        this.currentFloorPlan = JSON.parse(JSON.stringify(this.allFloorPlans[0]));
        this.originalFloorPlan = JSON.parse(JSON.stringify(this.allFloorPlans[0]));
      } else {
        this.currentFloorPlan = null;
        this.originalFloorPlan = null;
      }
    },
    async handleSaveName(newName) {
      this.saving = true;
      try {
        const currentObjects = this.preserveCurrentObjects();
        const updated = await this.api.updateFloorPlan(this.currentFloorPlan.id, { name: newName });

        this.updateFloorPlanState(updated, { preserveObjects: currentObjects });
        this.notifySuccess('msg.save.success');
      } catch (error) {
        if (error.response?.data?.error === 'duplicate_floor_plan_name') {
          this.notifyError('msg.save.duplicate_name');
        } else {
          this.notifyError('msg.save.failed');
        }
      } finally {
        this.saving = false;
      }
    },
    handleAdd() {
      if (!this.confirmDiscardChanges()) return;

      this.$refs.fileInput.click();
    },
    async handleFileSelected(event) {
      const file = event.target.files[0];
      if (!file) {
        return;
      }

      this.uploading = true;

      try {
        // 画像検証
        const validation = await validateImageFile(file, {
          allowedTypes: ['jpeg', 'png'],
          maxWidth: MAX_FLOOR_PLAN_IMAGE_SIZE,
          maxHeight: MAX_FLOOR_PLAN_IMAGE_SIZE,
        });

        if (!validation.isValid) {
          if (validation.reason === 'image_size_too_large') {
            this.notifyError('validation.image_size_too_large');
          } else {
            this.notifyError('validation.invalid_file_type');
          }
          return;
        }

        // 図面作成APIを呼び出してpresigned URLを取得
        const contentType = file.type;
        const floorPlanName = file.name.replace(/\.[^/.]+$/, ''); // 拡張子を除去
        const response = await this.api.createFloorPlan(this.placeId, {
          name: floorPlanName,
          contentType,
        });

        // S3に直接アップロード
        await this.api.uploadToPresignedUrl(response.uploadUrl, file);

        // ポーリング開始（非ブロッキング）
        this.pollingState = { id: response.floorPlanId };
        this.pollingStartTime = Date.now();
        this.pollingForNewFloorPlan = true;
      } catch (error) {
        if (error.response?.data?.error === 'duplicate_floor_plan_name') {
          this.notifyError('msg.upload.duplicate_name');
        } else {
          this.notifyError('msg.upload.failed');
        }
      } finally {
        this.uploading = false;
        event.target.value = '';
      }
    },
    async checkForNewFloorPlan() {
      // タイムアウトチェック
      if (Date.now() - this.pollingStartTime > POLLING_TIMEOUT_MS) {
        this.pollingForNewFloorPlan = false;
        this.notifyError('msg.upload_timeout');
        return this.pollingState;
      }

      try {
        const floorPlans = await this.api.fetchFloorPlans(this.placeId, { isBackgroundRequest: true });
        const newFloorPlan = floorPlans.find(fp => fp.id === this.pollingState.id);

        if (newFloorPlan && newFloorPlan.image_url) {
          // 全図面を更新
          this.allFloorPlans = floorPlans;
          return { id: this.pollingState.id, found: true, floorPlan: newFloorPlan };
        }
      } catch (error) {
        // 画面の自動更新ポーリングのため握りつぶす（次回インターバルで回復するためユーザー通知不要）
      }

      return this.pollingState;
    },
    onFloorPlanPollingUpdate(updatedState) {
      if (updatedState.found) {
        this.currentFloorPlan = JSON.parse(JSON.stringify(updatedState.floorPlan));
        this.originalFloorPlan = JSON.parse(JSON.stringify(updatedState.floorPlan));
        this.pollingForNewFloorPlan = false;
        this.notifySuccess('msg.upload.success');
      }
    },

    // === プライベートヘルパー ===
    preserveCurrentObjects() {
      return JSON.parse(JSON.stringify(this.currentFloorPlan.objects));
    },

    buildObjectsFromTraps() {
      const objects = {};
      this.trapList.forEach((trap) => {
        if (trap.isPlaced) {
          objects[trap.trapNo] = trap.coordinates;
        }
      });
      return objects;
    },

    updateFloorPlanState(updatedFloorPlan, options = {}) {
      const { preserveObjects } = options;

      // currentFloorPlan更新
      this.currentFloorPlan = preserveObjects
        ? { ...updatedFloorPlan, objects: preserveObjects }
        : JSON.parse(JSON.stringify(updatedFloorPlan));

      // originalFloorPlan更新
      this.originalFloorPlan = JSON.parse(JSON.stringify(updatedFloorPlan));

      // allFloorPlans更新
      this.updateFloorPlanInList(updatedFloorPlan);
    },

    updateFloorPlanInList(updatedFloorPlan) {
      const index = this.allFloorPlans.findIndex((fp) => fp.id === updatedFloorPlan.id);
      if (index !== -1) {
        this.$set(this.allFloorPlans, index, JSON.parse(JSON.stringify(updatedFloorPlan)));
      }
    },
  },
  mounted: async function () {
    try {
      this.place = await this.api.fetchStationPlace(this.placeId);

      this.allFloorPlans = await this.api.fetchFloorPlans(this.placeId);
      [this.currentFloorPlan] = this.allFloorPlans;

      // 取り消し用に元のデータを保持（ディープコピー）
      if (this.currentFloorPlan) {
        this.originalFloorPlan = JSON.parse(JSON.stringify(this.currentFloorPlan));
      }

      this.events = await this.api.fetchStationPlaceEvents(this.placeId, {
        from: this.selectedMonth,
        to: this.selectedMonth,
      });

      // ホバーポップアップ用に比較データを事前取得
      await this.fetchComparisonData();
    } catch (error) {
      this.notifyError('msg.load.failed');
    } finally {
      this.loading = false;
    }
  },
  beforeRouteLeave(to, from, next) {
    if (this.hasUnsavedChanges) {
      // eslint-disable-next-line no-alert
      const answer = window.confirm(this.$t('confirm.leave_page'));
      if (answer) {
        next();
      } else {
        next(false);
      }
    } else {
      next();
    }
  },
};
</script>

<style scoped>
.floor-plan-toolbar {
  position: sticky;
  top: 0;
  z-index: 20;
}

</style>
